/**
 * Resolves Rovo Insights API icon/color string keys into Atlaskit icon components
 * and IconTile appearances.
 *
 * The Rovo Insights API returns each insight group's visual identity as plain
 * string keys (`icon`, `color`). This module is the single source of truth for
 * turning those strings into renderable values, shared by:
 *  - the feed mapper ({@link ./mapApiResponseToInsightItems}), which resolves at
 *    fetch time for the in-panel InsightsFeed, and
 *  - external consumers (e.g. the home insights carousel via the
 *    `insights-open-in-chat` rovo-trigger), which only have the raw string keys
 *    available over the event bus and must re-resolve panel-side.
 *
 * Keeping resolution here guarantees both paths produce identical icons and
 * appearances, so the two surfaces cannot drift.
 */
import { type ComponentType, lazy } from 'react';

import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import type { IconTileProps, NewCoreIconProps } from '@atlaskit/icon/types';

import { COLOR_TO_APPEARANCE } from './COLOR_TO_APPEARANCE';
import { LAZY_ICON_MAP } from './LAZY_ICON_MAP';

const lazyIconCache = new Map<string, ComponentType<NewCoreIconProps>>();

/**
 * Resolves an API `icon` string key to a (lazy-loaded, cached) Atlaskit icon
 * component. Falls back to {@link LightbulbIcon} for unknown or missing keys.
 *
 * SSR: not supported. The returned component is `React.lazy`-wrapped, so it
 * suspends/throws during server-side rendering unless wrapped in a `<Suspense>`
 * boundary on the client. The insights surfaces that consume it (the in-panel
 * feed and the home carousel → chat panel) are client-only. Do not render the
 * result on the server without a client-side Suspense boundary.
 */
export function resolveInsightIcon(iconKey?: string): ComponentType<NewCoreIconProps> {
	const key = iconKey?.toLowerCase();
	const loader = key ? LAZY_ICON_MAP[key] : undefined;
	if (!key || !loader) {
		return LightbulbIcon;
	}
	const cachedIcon = lazyIconCache.get(key);
	if (cachedIcon) {
		return cachedIcon;
	}
	const icon = lazy(loader);
	lazyIconCache.set(key, icon);
	return icon;
}

/**
 * Resolves an API `color` string to an Atlaskit IconTile appearance.
 * Falls back to `'blueBold'` for unknown values.
 */
export function resolveInsightIconAppearance(color?: string): IconTileProps['appearance'] {
	return (color ? COLOR_TO_APPEARANCE[color] : undefined) ?? 'blueBold';
}
